import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download, Calendar, MousePointerClick, CheckCircle2, MinusCircle,
  Bot, Activity, Trophy
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis, Pie, PieChart, Cell
} from 'recharts';
import {
  getClickStats, getDailyClickStats, getTopModelsByClicks, getClickStatsV2
} from '../api/feed';
import PageHeader from '../components/shared/PageHeader';
import SubTabs from '../components/shared/SubTabs';
import { KpiSkeleton } from '../components/shared/TableSkeleton';
import { EmptyState } from '../components/shared/EmptyState';
import { KpiCard } from '../components/shared/KpiCard';
import { ChartTooltip, chartGrid, chartAxisProps, CHART_PALETTE } from '../components/shared/chartTheme';

const TRAFFIC_TABS = [
  { label: 'Overview', to: '/traffic',         end: true },
  { label: 'By page',  to: '/traffic/by-page'            }
];

const RANGES = [
  { value: 1, label: '1d' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' }
] as const;

const DOMAINS = [
  { value: '', label: 'all domains' },
  { value: 'main', label: 'main' },
  { value: 'osr', label: 'osr' },
  { value: 'o2s', label: 'o2s' },
  { value: 'sf', label: 'sf' },
  { value: 'fs', label: 'fs' }
] as const;

// PPC по дефолту — провайдер платит только за PPC. Остальные monetization
// группы (CPL/OF/REVSHARE) — для общей картины, можно явно переключить.
const MONETIZATIONS = [
  { value: 'PPC',      label: 'PPC' },
  { value: 'CPL',      label: 'CPL' },
  { value: 'OF',       label: 'OF' },
  { value: 'REVSHARE', label: 'RevShare' },
  { value: '',         label: 'all' }
] as const;

// Tier 1+2 по дефолту — это аудитория за которую партнёр платит. Tier 3
// (Латам/СНГ/Asia/Africa) обычно не оплачивается. Видно их в "all geo".
const TIERS = [
  { value: 'tier1,tier2', label: 'Tier 1+2 (paid)' },
  { value: 'tier1',       label: 'Tier 1' },
  { value: 'tier2',       label: 'Tier 2' },
  { value: '',            label: 'all geo' }
] as const;

const fmtPct = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';

const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat('en-US').format(n) : '—';

const fmtCompact = (n: number | null | undefined) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US').format(n);
};

const TrafficPage: React.FC = () => {
  const [range, setRange] = useState<number>(30);
  const [domain, setDomain] = useState<string>('');
  // Default: PPC + Tier1+2 — то что реально платят провайдеры.
  // Юзер может переключить чтобы посмотреть всё.
  const [monetization, setMonetization] = useState<string>('PPC');
  const [tiers, setTiers] = useState<string>('tier1,tier2');

  const { data: overall } = useQuery({ queryKey: ['clicks', 'stats'], queryFn: getClickStats });
  const { data: daily } = useQuery({ queryKey: ['clicks', 'daily', range], queryFn: () => getDailyClickStats(range) });
  const { data: topModels } = useQuery({ queryKey: ['clicks', 'top', range], queryFn: () => getTopModelsByClicks(20, range) });
  const { data: v2 } = useQuery({
    queryKey: ['clicks', 'v2', range, domain, monetization, tiers],
    queryFn: () => getClickStatsV2(range, {
      domain: domain || undefined,
      monetization: monetization || undefined,
      tiers: tiers || undefined
    })
  });

  const totalsByDay = useMemo(() => {
    if (!daily) return [];
    return daily.map(d => ({
      date: d.date,
      total: d.totalClicks,
      swipe: d.swipeClicks,
      other: d.otherClicks
    }));
  }, [daily]);

  const kpi = useMemo(() => {
    // Site-click tracker (swipe + other) — отдельная feed-service шкала.
    const total = totalsByDay.reduce((s, r) => s + r.total, 0);
    const swipe = totalsByDay.reduce((s, r) => s + r.swipe, 0);
    return {
      total,
      swipe,
      ratio: total > 0 ? (swipe / total) * 100 : 0
    };
  }, [totalsByDay]);

  // Tracking v2 — точные значения с воронкой redirected→confirmed/bounced.
  const v2kpi = useMemo(() => {
    if (!v2) return null;
    const s = v2.summary;
    return {
      total: s.total,
      confirmed: s.confirmed,
      bounced: s.bounced,
      bot: s.bot,
      duplicate: s.duplicate,
      ctr: s.ctr,
      bounceRate: s.bounceRate
    };
  }, [v2]);

  // Prefer v2.bySource (accurate, tracking_v2 click_events) over the legacy
  // overall.clicksBySource feed-service aggregate. Fall back to legacy when v2 is empty.
  const bySource = useMemo(() => {
    if (v2 && v2.bySource && v2.bySource.length > 0) {
      return [...v2.bySource]
        .map(r => ({ name: r.source || '—', value: r.total }))
        .sort((a, b) => b.value - a.value);
    }
    if (!overall?.clicksBySource) return [];
    return Object.entries(overall.clicksBySource)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [v2, overall]);

  const sourcesWithPct = useMemo(() => {
    const total = bySource.reduce((s, r) => s + r.value, 0) || 1;
    return bySource.map(r => ({
      ...r,
      pct: total > 0 ? `${Math.round((r.value / total) * 100)}%` : '—'
    }));
  }, [bySource]);

  // Promoted = PPC + CPL · Organic = everything else in byMonetization.
  // Returns null when there's no byMonetization data for this period.
  const promotedVsOrganic = useMemo(() => {
    if (!v2 || !v2.byMonetization || v2.byMonetization.length === 0) return null;
    let promoted = 0;
    let total = 0;
    for (const m of v2.byMonetization) {
      total += m.total;
      if (m.monetizationGroup === 'PPC' || m.monetizationGroup === 'CPL') {
        promoted += m.total;
      }
    }
    if (total === 0) return null;
    const organic = total - promoted;
    return {
      promoted,
      organic,
      promotedPct: Math.round((promoted / total) * 100),
      organicPct: Math.round((organic / total) * 100)
    };
  }, [v2]);

  const COLORS = CHART_PALETTE;

  const actions = (
    <>
      <button className="hf-btn"><Calendar size={14} /> {RANGES.find(r => r.value === range)?.label}</button>
      <button className="hf-btn"><Download size={14} /> Export CSV</button>
    </>
  );

  return (
    <>
      <PageHeader
        title="Traffic"
        subtitle="Click funnel (tracking v2) · feed-service site clicks · bot breakdown"
        actions={actions}
      />

      <div className="hf-content">
        <SubTabs items={TRAFFIC_TABS} />
        {/* Filter toolbar */}
        <div className="hf-toolbar">
          <div className="hf-segmented">
            {RANGES.map(r => (
              <button key={r.value} className={range === r.value ? 'on' : ''} onClick={() => setRange(r.value)}>{r.label}</button>
            ))}
          </div>
          <div className="hf-segmented">
            {DOMAINS.map(d => (
              <button key={d.value} className={domain === d.value ? 'on' : ''} onClick={() => setDomain(d.value)}>{d.label}</button>
            ))}
          </div>
          <div className="hf-segmented">
            {MONETIZATIONS.map(m => (
              <button key={m.value} className={monetization === m.value ? 'on' : ''} onClick={() => setMonetization(m.value)}>{m.label}</button>
            ))}
          </div>
          <div className="hf-segmented">
            {TIERS.map(t => (
              <button key={t.value} className={tiers === t.value ? 'on' : ''} onClick={() => setTiers(t.value)}>{t.label}</button>
            ))}
          </div>
          <span className="hf-help" style={{ marginTop: 0, marginLeft: 'auto' }}>
            vs previous {range}d · tracking v2
          </span>
        </div>

        {/* KPI strip — Tracking v2 funnel */}
        {!v2kpi && <KpiSkeleton count={5} />}
        {v2kpi && (
          <div className="hf-kpi-strip">
            <KpiCard
              accent
              Icon={MousePointerClick}
              label="Clicks (v2)"
              value={fmtCompact(v2kpi.total)}
              sub="redirected + confirmed + bounced"
              spark={v2?.daily?.map(d => d.total)}
            />
            <KpiCard
              Icon={CheckCircle2}
              label="Confirmed"
              value={fmtCompact(v2kpi.confirmed)}
              sub={<>CTR <span className="hf-mono">{fmtPct(v2kpi.ctr)}</span></>}
              spark={v2?.daily?.map(d => d.confirmed)}
            />
            <KpiCard
              Icon={MinusCircle}
              label="Bounced"
              value={fmtCompact(v2kpi.bounced)}
              sub={<><span className="hf-mono">{fmtPct(v2kpi.bounceRate)}</span> of clicks</>}
              spark={v2?.daily?.map(d => d.bounced)}
            />
            <KpiCard
              Icon={Bot}
              label="Bots / Dup"
              value={fmtCompact((v2kpi.bot ?? 0) + (v2kpi.duplicate ?? 0))}
              sub={<><span className="hf-mono">{fmt(v2kpi.bot)}</span> bot · <span className="hf-mono">{fmt(v2kpi.duplicate)}</span> dup</>}
              spark={v2?.daily?.map(d => (d.bot ?? 0) + (d.duplicate ?? 0))}
            />
            <KpiCard
              Icon={Activity}
              label="Site clicks"
              value={fmtCompact(kpi.total)}
              sub={<>feed swipe + other</>}
              spark={totalsByDay.map(d => d.total)}
            />
          </div>
        )}

        {/* Tracking v2 — bot traffic breakdown */}
        {v2 && v2.botTraffic && (v2.botTraffic.topSignatures.length > 0 || v2.botTraffic.byType.length > 0) && (
          <div className="hf-two-col narrow-right" style={{ marginBottom: 16 }}>
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Top bots · {range}d</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>honeypot 302 — bot still redirects to OF, but is excluded from CTR</span>
              </div>
              {v2.botTraffic.topSignatures.length === 0 ? (
                <div className="hf-card-body" style={{ color: 'var(--fg-3)' }}>No bot traffic yet.</div>
              ) : (
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th>signature</th>
                      <th>type</th>
                      <th className="num">hits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v2.botTraffic.topSignatures.map(s => (
                      <tr key={`${s.signature}/${s.type}`}>
                        <td><span className="hf-mono">{s.signature}</span></td>
                        <td><span className="hf-mono" style={{ color: 'var(--fg-3)' }}>{s.type}</span></td>
                        <td className="num">{fmt(s.hits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">By bot type</h3>
              </div>
              {v2.botTraffic.byType.length === 0 ? (
                <div className="hf-card-body" style={{ color: 'var(--fg-3)' }}>—</div>
              ) : (() => {
                const totalBotHits = v2.botTraffic.byType.reduce((s, t) => s + t.hits, 0) || 1;
                return (
                  <table className="hf-table">
                    <thead>
                      <tr>
                        <th>type</th>
                        <th>distribution</th>
                        <th className="num">hits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v2.botTraffic.byType.map(t => {
                        const pct = (t.hits / totalBotHits) * 100;
                        return (
                          <tr key={t.type}>
                            <td><span className="hf-mono">{t.type}</span></td>
                            <td style={{ width: 140 }}>
                              <div className="hf-bar-track">
                                <div className="hf-bar-fill" style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                            <td className="num">{fmt(t.hits)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        )}

        {/* Heatmap: activity by hour × day-of-week (confirmed clicks) */}
        {v2 && v2.heatmap && v2.heatmap.length > 0 && (
          <div className="hf-card" style={{ marginBottom: 16 }}>
            <div className="hf-card-header">
              <h3 className="hf-card-title">Activity heatmap · {range}d</h3>
              <span className="hf-help" style={{ marginTop: 0 }}>confirmed clicks, MSK timezone</span>
            </div>
            <div className="hf-card-body">
              {(() => {
                const maxCount = Math.max(...v2.heatmap.map(c => c.confirmed), 1);
                const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
                for (const c of v2.heatmap) grid[c.dow][c.hour] = c.confirmed;
                const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const cellSize = 18;
                const gridStep = 3;
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'inline-block' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `44px repeat(24, ${cellSize}px)`,
                          gap: 3,
                          fontSize: 10,
                          color: 'var(--fg-3)',
                          fontFamily: 'var(--font-mono)'
                        }}
                      >
                        <div />
                        {Array.from({ length: 24 }, (_, h) => (
                          <div key={h} style={{ textAlign: 'center', width: cellSize, height: 14 }}>
                            {h % gridStep === 0 ? h : ''}
                          </div>
                        ))}
                        {days.map((dayLabel, dow) => (
                          <React.Fragment key={dayLabel}>
                            <div style={{ textAlign: 'right', paddingRight: 6, lineHeight: `${cellSize}px`, fontFamily: 'var(--font-sans)' }}>
                              {dayLabel}
                            </div>
                            {Array.from({ length: 24 }, (_, h) => {
                              const v = grid[dow][h];
                              const intensity = v === 0 ? 0 : 0.18 + 0.82 * Math.sqrt(v / maxCount);
                              const bg = v === 0
                                ? 'var(--surface-2)'
                                : `color-mix(in oklab, var(--accent) ${(intensity * 100).toFixed(0)}%, transparent)`;
                              return (
                                <div
                                  key={h}
                                  title={`${dayLabel} ${h}:00 — ${v} clicks`}
                                  style={{
                                    width: cellSize,
                                    height: cellSize,
                                    background: bg,
                                    border: '1px solid var(--surface)',
                                    borderRadius: 3
                                  }}
                                />
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                      <div
                        style={{
                          marginTop: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          fontSize: 11,
                          color: 'var(--fg-3)'
                        }}
                      >
                        <span>Less</span>
                        <div style={{ display: 'inline-flex', gap: 2 }}>
                          {[0.1, 0.3, 0.5, 0.7, 0.9].map(p => (
                            <span
                              key={p}
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                background: `color-mix(in oklab, var(--accent) ${p * 100}%, transparent)`,
                                border: '1px solid var(--border)'
                              }}
                            />
                          ))}
                        </div>
                        <span>More</span>
                        <span style={{ marginLeft: 'auto' }}>
                          Peak: <span className="hf-mono" style={{ color: 'var(--fg-2)' }}>{fmt(maxCount)}</span> clicks/hour
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Tracking v2 — daily timeseries + device split */}
        {v2 && (v2.daily?.length > 0 || v2.byDevice?.length > 0) && (
          <div className="hf-two-col narrow-right" style={{ marginBottom: 16 }}>
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Daily funnel · {range}d</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>real-clicks vs bot/dup</span>
              </div>
              <div className="hf-card-body" style={{ height: 220 }}>
                {!v2.daily || v2.daily.length === 0 ? (
                  <EmptyState compact title="No daily data" description={`No clicks in the last ${range}d.`} />
                ) : (
                  <ResponsiveContainer>
                    <AreaChart data={v2.daily} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gConfirmed" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%"   stopColor="var(--accent)" stopOpacity={0.32} />
                          <stop offset="100%" stopColor="var(--accent)" stopOpacity={0}    />
                        </linearGradient>
                      </defs>
                      <CartesianGrid {...chartGrid} />
                      <XAxis dataKey="date" {...chartAxisProps} tickFormatter={(v) => v.slice(5)} />
                      <YAxis {...chartAxisProps} tickFormatter={fmtCompact} width={42} />
                      <Tooltip
                        content={<ChartTooltip formatter={(v) => fmt(Number(v))} />}
                        cursor={{ stroke: 'var(--border-2)', strokeDasharray: '3 3' }}
                      />
                      <Area type="monotone" dataKey="confirmed" name="Confirmed" stroke="var(--accent)" strokeWidth={2} fill="url(#gConfirmed)" />
                      <Area type="monotone" dataKey="bot" name="Bots" stroke="var(--fg-3)" strokeWidth={1.25} fill="transparent" strokeDasharray="3 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">By device</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>YAUAA classified</span>
              </div>
              {(!v2.byDevice || v2.byDevice.length === 0) ? (
                <div className="hf-card-body" style={{ color: 'var(--fg-3)' }}>—</div>
              ) : (
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th>device</th>
                      <th className="num">clicks</th>
                      <th className="num">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v2.byDevice.map(d => (
                      <tr key={d.deviceClass}>
                        <td><span className="hf-mono">{d.deviceClass}</span></td>
                        <td className="num">{fmt(d.total)}</td>
                        <td className="num hf-mono">{fmtPct(d.total > 0 ? d.confirmed / d.total : null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Tracking v2 — breakdown by monetization group */}
        {v2 && v2.byMonetization.length > 0 && (
          <div className="hf-card" style={{ marginBottom: 16 }}>
            <div className="hf-card-header">
              <h3 className="hf-card-title">By monetization · v2</h3>
              <span className="hf-help" style={{ marginTop: 0 }}>snapshot from click_events</span>
            </div>
            <table className="hf-table">
              <thead>
                <tr>
                  <th>group</th>
                  <th className="num">clicks</th>
                  <th className="num">confirmed</th>
                  <th className="num">bounced</th>
                  <th className="num">CTR</th>
                  <th className="num">bounce%</th>
                  <th className="num">bot</th>
                  <th className="num">dup</th>
                </tr>
              </thead>
              <tbody>
                {v2.byMonetization.map(m => {
                  const ctr = m.total > 0 ? m.confirmed / m.total : 0;
                  const br = m.total > 0 ? m.bounced / m.total : 0;
                  return (
                    <tr key={m.monetizationGroup}>
                      <td><span className="hf-mono">{m.monetizationGroup}</span></td>
                      <td className="num">{fmt(m.total)}</td>
                      <td className="num">{fmt(m.confirmed)}</td>
                      <td className="num">{fmt(m.bounced)}</td>
                      <td className="num">{fmtPct(ctr)}</td>
                      <td className="num">{fmtPct(br)}</td>
                      <td className="num" style={{ color: 'var(--fg-3)' }}>{fmt(m.bot)}</td>
                      <td className="num" style={{ color: 'var(--fg-3)' }}>{fmt(m.duplicate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Row: chart + top models leaderboard */}
        <div className="hf-two-col wide-right">
          <div className="hf-card">
            <div className="hf-card-header">
              <h3 className="hf-card-title">Site clicks · daily</h3>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--fg-2)', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 14, height: 2, background: 'var(--fg-1)', borderRadius: 2 }} /> Total
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 14, height: 2, background: 'var(--accent)', borderRadius: 2 }} /> Swipe
                </span>
              </div>
            </div>
            <div className="hf-card-body" style={{ height: 280 }}>
              {totalsByDay.length === 0 ? (
                <EmptyState compact title="No clicks recorded yet" description="Site click data shows here once traffic comes in." />
              ) : (
                <ResponsiveContainer>
                  <AreaChart data={totalsByDay} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gSwipe" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%"   stopColor="var(--accent)" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="var(--accent)" stopOpacity={0}    />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...chartGrid} />
                    <XAxis dataKey="date" {...chartAxisProps} tickFormatter={(v) => v.slice(5)} />
                    <YAxis {...chartAxisProps} tickFormatter={fmtCompact} width={42} />
                    <Tooltip
                      content={<ChartTooltip formatter={(v) => fmt(Number(v))} />}
                      cursor={{ stroke: 'var(--border-2)', strokeDasharray: '3 3' }}
                    />
                    <Area type="monotone" dataKey="total" name="Total"  stroke="var(--fg-1)"   strokeWidth={2}    fill="transparent" />
                    <Area type="monotone" dataKey="swipe" name="Swipe"  stroke="var(--accent)" strokeWidth={1.75} fill="url(#gSwipe)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top models leaderboard */}
          <div className="hf-card flush">
            <div className="hf-card-header">
              <h3 className="hf-card-title">
                <Trophy size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px', color: 'var(--warning)' }} />
                Top models · {range}d
              </h3>
            </div>
            {!topModels || topModels.length === 0 ? (
              <div style={{ padding: 16 }}>
                <EmptyState compact Icon={Trophy} title="No top models yet" description="As clicks land, this leaderboard fills up." />
              </div>
            ) : (
              <table className="hf-table" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }} className="num">#</th>
                    <th>model</th>
                    <th className="num">clicks</th>
                    <th className="num">swipe</th>
                  </tr>
                </thead>
                <tbody>
                  {topModels.slice(0, 14).map((m, i) => {
                    const isPodium = i < 3;
                    const podiumBg = i === 0 ? 'var(--warning-soft)'
                      : i === 1 ? 'var(--accent-soft)'
                        : i === 2 ? 'var(--success-soft)'
                          : undefined;
                    const podiumColor = i === 0 ? 'var(--warning)'
                      : i === 1 ? 'var(--accent)'
                        : i === 2 ? 'var(--success)'
                          : 'var(--fg-3)';
                    return (
                      <tr key={m.modelId} style={isPodium ? { background: podiumBg } : undefined}>
                        <td className="num">
                          <span
                            className="hf-mono"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 22, height: 22,
                              borderRadius: '50%',
                              background: isPodium ? 'var(--surface)' : 'transparent',
                              border: isPodium ? `1px solid ${podiumColor}` : 'none',
                              color: podiumColor,
                              fontWeight: 600,
                              fontSize: 11
                            }}
                          >
                            {i + 1}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="hf-avatar" style={{ width: 22, height: 22, fontSize: 10 }}>
                              {(m.modelName || m.modelUsername || '?').trim().charAt(0).toUpperCase()}
                            </span>
                            <span className="hf-mono" style={{ fontWeight: 500, color: 'var(--fg-1)' }}>
                              @{m.modelUsername}
                            </span>
                          </div>
                        </td>
                        <td className="num hf-mono" style={{ fontWeight: 600, color: 'var(--fg-1)' }}>
                          {fmtCompact(m.totalClicks)}
                        </td>
                        <td className="num hf-mono" style={{ color: 'var(--fg-2)' }}>
                          {fmtCompact(m.swipeClicks)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Row: sources + promoted vs organic */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="hf-card">
            <div className="hf-card-header"><h3 className="hf-card-title">Sources</h3></div>
            <div className="hf-card-body" style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              {bySource.length === 0 ? (
                <div style={{ color: 'var(--fg-3)' }}>No source data.</div>
              ) : (
                <>
                  <div style={{ width: 160, height: 160, flex: '0 0 160px', position: 'relative' }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={bySource}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={56}
                          outerRadius={78}
                          paddingAngle={2}
                          stroke="var(--surface)"
                          strokeWidth={2}
                        >
                          {bySource.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip
                          content={<ChartTooltip formatter={(v) => fmtCompact(Number(v))} />}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div
                      style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none'
                      }}
                    >
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 19, fontWeight: 600,
                        color: 'var(--fg-1)', letterSpacing: '-0.02em'
                      }}>
                        {fmtCompact(bySource.reduce((s, r) => s + r.value, 0))}
                      </span>
                      <span style={{ fontSize: 10.5, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Total
                      </span>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sourcesWithPct.map((r, i) => (
                      <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length], flex: '0 0 10px' }} aria-hidden="true" />
                        <span style={{ flex: 1, color: 'var(--fg-2)' }}>{r.name}</span>
                        <span className="hf-mono" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{fmtCompact(r.value)}</span>
                        <span className="hf-mono" style={{ width: 42, textAlign: 'right', color: 'var(--fg-3)' }}>{r.pct}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="hf-card">
            <div className="hf-card-header"><h3 className="hf-card-title">Promoted vs organic</h3></div>
            <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!promotedVsOrganic ? (
                <span className="hf-help" style={{ marginTop: 0 }}>No monetization data for this period.</span>
              ) : (
                <>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span>Promoted</span>
                      <span className="hf-mono">{fmtCompact(promotedVsOrganic.promoted)} · {promotedVsOrganic.promotedPct}%</span>
                    </div>
                    <div className="hf-bar-track" style={{ height: 8 }}>
                      <div className="hf-bar-fill" style={{ width: `${promotedVsOrganic.promotedPct}%` }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span>Organic</span>
                      <span className="hf-mono">{fmtCompact(promotedVsOrganic.organic)} · {promotedVsOrganic.organicPct}%</span>
                    </div>
                    <div className="hf-bar-track" style={{ height: 8 }}>
                      <div className="hf-bar-fill" style={{ width: `${promotedVsOrganic.organicPct}%`, background: 'var(--fg-1)' }} />
                    </div>
                  </div>
                  <span className="hf-help" style={{ marginTop: 4 }}>
                    Promoted = PPC + CPL · Organic = REVSHARE + non-monetized (from byMonetization v2)
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default TrafficPage;
